import { /*Map,*/ fromJS } from "immutable";
import { handleActions, createAction } from "redux-actions";
import Service from "lib/service-amp";
import config from "lib/fr-config";
import { sleep } from "lib/commonUtil";
import { getLoginProvider } from "lib/tokenAuthParser";
import { sendError, sendMessage } from "lib/sentryHelper";

const TOKEN_RENEW_CHECK_INTERVAL_MIN = 1;
const TOKEN_RENEWABLE_MIN = 5;
const FRESH_TOKEN_OFFSET_SEC = 60;
const WAIT_MILLISEC = config.storeModule.waitForLoadingMilliSec;

const SET_IS_TOKEN_LOADING = "cognitoAuth/SET_IS_TOKEN_LOADING";
const SET_TOKEN_OBJ = "cognitoAuth/SET_TOKEN_OBJ";
const SET_COG_TOKEN_EXP_TIME_UTC = "cognitoAuth/SET_COG_TOKEN_EXP_TIME_UTC";
const SET_INTERVAL_ID = "cognitoAuth/SET_INTERVAL_ID";
const SET_NEW_ID_TOKEN = "cognitoAuth/SET_NEW_ID_TOKEN";
const SET_USER_AUTH_DATA = "cognitoAuth/SET_USER_AUTH_DATA";
const SET_TOKEN_AUTH_TYPE = "cognitoAuth/SET_TOKEN_AUTH_TYPE";
const SET_TOKEN_REFRESH_ERROR = "cognitoAuth/SET_TOKEN_REFRESH_ERROR";
const INIT_ALL_DATA = "cognitoAuth/INIT_ALL_DATA";

const setIsTokenLoading = createAction(SET_IS_TOKEN_LOADING);
const setTokenObj = createAction(SET_TOKEN_OBJ);
const setCogTokenExpTimeUtc = createAction(SET_COG_TOKEN_EXP_TIME_UTC);
const setIntervalId = createAction(SET_INTERVAL_ID);
const setNewIdToken = createAction(SET_NEW_ID_TOKEN);
const setUserAuthData = createAction(SET_USER_AUTH_DATA);
const setTokenAuthType = createAction(SET_TOKEN_AUTH_TYPE);
const setTokenRefreshError = createAction(SET_TOKEN_REFRESH_ERROR);
const initAllData = createAction(INIT_ALL_DATA);

const getCognitoAuthState = ({ getState }) => {
  const cognitoAuthState = getState().cognitoAuth.toJS();
  return cognitoAuthState;
};

const getIsLoading = ({ getState }) => {
  const cognitoAuthState = getCognitoAuthState({ getState });
  const { isTokenLoading } = cognitoAuthState;
  return isTokenLoading;
};

const getRemainMin = ({ getState }) => {
  const cognitoAuthState = getCognitoAuthState({ getState });
  const { cogTokenExpTimeUtc } = cognitoAuthState;
  const expDateObj = new Date(cogTokenExpTimeUtc);
  const nowDateObj = new Date();
  const remainMin = Math.floor((expDateObj - nowDateObj) / 1000 / 60);
  return remainMin;
};

const terminateTokenRenewFn = ({ getState }) => {
  const cognitoAuthState = getCognitoAuthState({ getState });
  const { intervalId } = cognitoAuthState;
  if (intervalId) clearInterval(intervalId);
};

const setExpTimeOfToken = ({ dispatch, idToken }) => {
  const expTimeUtc = new Date(idToken.getExpiration() * 1000).toISOString();
  dispatch(setCogTokenExpTimeUtc(expTimeUtc));
};

const writeAuthType = ({ tokenObj, dispatch }) => {
  const accessToken = tokenObj.accessToken;
  const { tokenAuthType, warning, error } = getLoginProvider({ accessToken });
  if (error) {
    sendError(error);
  } else if (warning) {
    sendMessage(error);
  }
  dispatch(setTokenAuthType(tokenAuthType));
};

const writeTokenData = ({ tokenObj, dispatch }) => {
  dispatch(setTokenObj(tokenObj));
  const idToken = tokenObj.idToken;
  setExpTimeOfToken({ dispatch, idToken });
  dispatch(setNewIdToken(idToken.getJwtToken()));
  writeAuthType({ tokenObj, dispatch });
};

const updateToken = async ({ dispatch, getState }) => {
  const { code, res } = await Service.renewToken();
  if (code === "error") {
    throw Error(res);
  } else if (!res) {
    dispatch(setTokenRefreshError(true));
    return { isUpdated: false };
  }

  const tokenObj = res;
  writeTokenData({ tokenObj, dispatch });
  return { isUpdated: true };
};

const getTokenRenewCallbackFunction =
  ({ dispatch, getState }) =>
  async () => {
    const remainMin = getRemainMin({ getState });
    if (remainMin <= TOKEN_RENEWABLE_MIN) {
      await updateToken({ dispatch, getState });
    }
  };

const initTokenRenewLoopFn = ({ dispatch, getState }) => {
  terminateTokenRenewFn({ getState });
  const intervalId = setInterval(
    getTokenRenewCallbackFunction({ dispatch, getState }),
    TOKEN_RENEW_CHECK_INTERVAL_MIN * 60 * 1000
  );
  dispatch(setIntervalId(intervalId));
};

export const setToken =
  ({ tokenObj }) =>
  (dispatch, getState) => {
    writeTokenData({ tokenObj, dispatch });
    initTokenRenewLoopFn({ dispatch, getState });
  };

export const forceUpdateToken = () => async (dispatch, getState) => {
  const { isUpdated } = await updateToken({ dispatch, getState });
  return { isUpdated };
};

export const getFreshIdToken = () => async (dispatch, getState) => {
  let isTokenLoading = getIsLoading({ getState });

  while (isTokenLoading) {
    await sleep(WAIT_MILLISEC);
    isTokenLoading = getIsLoading({ getState });
  }

  dispatch(setIsTokenLoading(true));
  let idToken;
  let hasTokenExpired = false;

  const { cognitoIdToken, cogTokenExpTimeUtc } = getCognitoAuthState({
    getState
  });

  const expTimeObj = new Date(cogTokenExpTimeUtc);
  const nowTimeObj = new Date();

  if (expTimeObj - nowTimeObj > FRESH_TOKEN_OFFSET_SEC * 1000) {
    idToken = cognitoIdToken;
  } else {
    const { isUpdated } = await updateToken({ dispatch, getState });

    if (isUpdated) {
      const { cognitoIdToken } = getCognitoAuthState({ getState });
      idToken = cognitoIdToken;
    } else {
      hasTokenExpired = true;
    }
  }

  dispatch(setIsTokenLoading(false));
  return { idToken, hasTokenExpired };
};

export const setAuthData =
  ({ isEmailAuthUser, isGoogleAuthUser }) =>
  (dispatch, getState) => {
    dispatch(setUserAuthData({ isEmailAuthUser, isGoogleAuthUser }));
  };

export const deleteAllData = () => (dispatch, getState) => {
  terminateTokenRenewFn({ getState });
  dispatch(initAllData());
};

const initialState = fromJS({
  data: { tokenObj: null },
  /*
  data 의 구조.
  data: {
      tokenObj: {
        idToken: {
          jwtToken: 'eyJraWQi~',
          payload: {
            sub: 'e61336f9-4f36-4da2-87be-970019d9a129',
            aud: '6eur5ep7a00fum2drkhepmdggc',
            email_verified: false,
            event_id: 'ea321f81-f791-40bd-80dc-f8be79c65624',
            token_use: 'id',
            auth_time: 1623546276,
            iss: 'https://cognito-idp.us-east-1.amazonaws.com/us-east-1_DYgyRRZMl',
            'cognito:username': 'e61336f9-4f36-4da2-87be-970019d9a129',
            exp: 1623674592,
            iat: 1623670992,
            email: 'aman@test.com'
          },
          // 그외 기타 함수들..
        },
        accessToken: {
          jwtToken: 'eyJraWQi~',
          payload: {
            sub: 'e61336f9-4f36-4da2-87be-970019d9a129',
            event_id: 'ea321f81-f791-40bd-80dc-f8be79c65624',
            token_use: 'access',
            scope: 'aws.cognito.signin.user.admin',
            auth_time: 1623546276,
            iss: 'https://cognito-idp.us-east-1.amazonaws.com/us-east-1_DYgyRRZMl',
            exp: 1623674592,
            iat: 1623670992,
            jti: 'd1b91c18-2f5e-4f29-b00a-4e8c5a25be1d',
            client_id: '6eur5ep7a00fum2drkhepmdggc',
            username: 'e61336f9-4f36-4da2-87be-970019d9a129'
          },
          // 그외 기타 함수들..
        }
      }
    },
  */

  isTokenLoading: false,

  cogTokenExpTimeUtc: null,

  userAuthData: {
    /**
     * isAuthDataUpdated: 하단 변수가 기록되었으면 true.
     * 하단변수 단독사용X. isAuthDataUpdated와 같이 사용하자
     * isEmailAuthUser: 이메일 로그인으로 회원가입한 사용자.
     * isGoogleAuthUser: 구글 로그인으로 회원가입한 사용자.
     * (이메일회원이 구글로그인으로 중복 가능. 링크로 두계정이 한명으로 연결됨.)
     * */
    isAuthDataUpdated: false,
    isEmailAuthUser: false,
    isGoogleAuthUser: false
  },
  tokenAuthType: null, // "google", "cognito_email", null 중 1개.

  cognitoIdToken: null,
  cognitoTokenRefreshError: false,
  intervalId: null
});

export default handleActions(
  {
    [SET_IS_TOKEN_LOADING]: (state, action) => {
      const isTokenLoading = action.payload;
      return state.set("isTokenLoading", fromJS(isTokenLoading));
    },
    [SET_TOKEN_OBJ]: (state, action) => {
      const tokenObj = action.payload;
      return state.setIn(["data", "tokenObj"], fromJS(tokenObj));
    },
    [SET_COG_TOKEN_EXP_TIME_UTC]: (state, action) => {
      const cogTokenExpTimeUtc = action.payload;
      return state.set("cogTokenExpTimeUtc", fromJS(cogTokenExpTimeUtc));
    },
    [SET_INTERVAL_ID]: (state, action) => {
      const intervalId = action.payload;
      return state.set("intervalId", fromJS(intervalId));
    },
    [SET_USER_AUTH_DATA]: (state, action) => {
      const { isEmailAuthUser, isGoogleAuthUser } = action.payload;
      const userAuthData = {
        isAuthDataUpdated: true,
        isEmailAuthUser,
        isGoogleAuthUser
      };
      return state.set("userAuthData", fromJS(userAuthData));
    },
    [SET_TOKEN_AUTH_TYPE]: (state, action) => {
      const tokenAuthType = action.payload;
      return state.set("tokenAuthType", fromJS(tokenAuthType));
    },
    [SET_NEW_ID_TOKEN]: (state, action) => {
      const cognitoIdToken = action.payload;
      return state.set("cognitoIdToken", fromJS(cognitoIdToken));
    },
    [SET_TOKEN_REFRESH_ERROR]: (state, action) => {
      const cognitoTokenRefreshError = action.payload;
      return state.set(
        "cognitoTokenRefreshError",
        fromJS(cognitoTokenRefreshError)
      );
    },
    [INIT_ALL_DATA]: (state, action) => {
      return initialState;
    }
  },
  initialState
);
